/**
 * FunnelManagerPage — the nurture automation surface (Marketing Emails).
 *
 * Split out of FreeTierMembersTab on 2026-08-19. That component was a
 * 1,000-line page under "Members & Conversion" that fused two unrelated jobs:
 * free-tier member intelligence (who is where) and the nurture funnel machinery
 * (what we send them). The machinery belongs with the rest of the email system,
 * so an operator editing a sequence is one click from its templates, its
 * audience and its performance.
 *
 *   Activation Pipeline ribbon — where Members sit, which funnel owns each phase
 *   Nurture Funnel Controls    — every funnel PRE and POST, activate/pause,
 *                                re-time steps, attach offers, edit templates
 *
 * Every step tile names the template it sends and links straight to that
 * template's performance page; every funnel names the audience that mirrors its
 * entry rule (migration 1102) and links to it.
 *
 * Data: GET /api/admin/free-tier/activation-pipeline + /api/admin/email-funnels/*
 * Deep link: ?flow=<slug> pre-expands one funnel (used by legacy /funnels/:slug).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Button, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Collapse, Switch, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Select, FormControl, InputLabel, FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon, Close as CloseIcon, Delete as DeleteIcon,
  ContentCopy as CopyIcon, ArrowBack as ArrowLeftIcon, ArrowForward as ArrowRightIcon,
  Add as AddIcon, InsightsOutlined as InsightsIcon, GroupOutlined as GroupIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../../../services/api';
import PillarPageShell from '../../shared/PillarPageShell';
import MarketingTemplateEditorDialog from '../MarketingTemplateEditorDialog';
import V4ArchitecturePanel from './V4ArchitecturePanel';
import {
  PHASE_COLOR, INK, SLATE, LINE, TIER_COLOR,
  Funnel, FunnelStep, Phase, EligibleMemberRow,
  funnelBadge, funnelDisplayName, funnelGroup, isOfferCapable, orderFunnels,
  phaseLabel, templatePerformancePath, audienceDetailPath,
} from './funnelShared';

// The pre/post hard line: a labeled section divider between conversion and retention.
const SectionDivider: React.FC<{ label: string; sublabel: string }> = ({ label, sublabel }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, mb: 0.5 }}>
    <Box sx={{ height: 2, flex: 1, bgcolor: '#E7DECB' }} />
    <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
      <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: '#1A5F7A' }}>{label}</Typography>
      <Typography sx={{ fontSize: 11, color: '#5D6D7E' }}>{sublabel}</Typography>
    </Box>
    <Box sx={{ height: 2, flex: 1, bgcolor: '#E7DECB' }} />
  </Box>
);

export default function FunnelManagerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pipeline, setPipeline] = useState<{ kpis: any; phases: Phase[]; active_funnels: number; total_funnels: number } | null>(null);
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);
  const [editTemplate, setEditTemplate] = useState<{ open: boolean; templateId: string | null }>({ open: false, templateId: null });
  const [eligibleDialog, setEligibleDialog] = useState<Funnel | null>(null);
  const [eligibleMembers, setEligibleMembers] = useState<EligibleMemberRow[]>([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [stepsDialog, setStepsDialog] = useState<Funnel | null>(null);
  const [addStepDialog, setAddStepDialog] = useState<{ open: boolean; funnel: Funnel | null }>({ open: false, funnel: null });
  const [addStepForm, setAddStepForm] = useState({ step_type: 'send_email', label: '', delay_hours: 0, include_offer: false, template_option: 'new', template_key: '' });
  const [addStepLoading, setAddStepLoading] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<Array<{ id: string; template_key: string; name: string }>>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; funnel: Funnel | null; step: FunnelStep | null }>({ open: false, funnel: null, step: null });
  const [promoDialog, setPromoDialog] = useState<{ open: boolean; funnel: Funnel | null; code: string }>({ open: false, funnel: null, code: '' });

  const loadPipelineAndFunnels = useCallback(async () => {
    try {
      const [pipeRes, funRes] = await Promise.all([
        apiService.getActivationPipeline(),
        apiService.getEmailFunnelsOverview(),
      ]);
      setPipeline((pipeRes as any).data);
      // funRes is the typed EmailFunnelsOverview ({ success, summary, flows }).
      const allFlows: Funnel[] = funRes?.flows || [];
      // Ordered PRE-then-POST by marketing number (F1…F7, then P1…P4).
      // The previous hand-picked list named only the phase funnels plus two
      // slugs, so F3 and F6b — both active and enrolling — never appeared.
      setFunnels(orderFunnels(allFlows));
    } catch (e: any) {
      setToast({ msg: e?.message || 'Failed to load pipeline', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);
  const openEligibleDialog = useCallback(async (funnel: Funnel) => {
    setEligibleDialog(funnel);
    setEligibleMembers([]);
    setEligibleLoading(true);
    try {
      const data = await apiService.getFunnelEligibleMembers(funnel.id);
      setEligibleMembers(data?.members || []);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Failed to load eligible members', sev: 'error' });
    } finally {
      setEligibleLoading(false);
    }
  }, []);

  useEffect(() => { loadPipelineAndFunnels(); }, [loadPipelineAndFunnels]);

  // ?flow=<slug> — legacy /communications/funnels/:slug links and the
  // "open in Funnel Manager" affordances elsewhere land here pre-expanded.
  const deepLinkSlug = searchParams.get('flow');
  useEffect(() => {
    if (!deepLinkSlug || !funnels.length) return;
    const match = funnels.find((f) => f.slug === deepLinkSlug);
    if (match) setExpanded(match.id);
  }, [deepLinkSlug, funnels]);

  const toggleFunnel = async (f: Funnel) => {
    const next = f.status === 'active' ? 'paused' : 'active';
    try {
      await apiService.toggleEmailFunnel(f.id, next as 'active' | 'paused');
      setToast({ msg: `${f.name} ${next === 'active' ? 'activated' : 'paused'}`, sev: 'success' });
      loadPipelineAndFunnels();
    } catch (e: any) {
      // 409 stub-block surfaces here with the server's reason.
      setToast({ msg: e?.message || 'Could not change funnel status', sev: 'error' });
    }
  };

  const saveStepDelay = async (f: Funnel, step: FunnelStep, hours: number) => {
    try {
      await apiService.updateFunnelSteps(f.id, [{ id: step.id, delay_hours: hours }]);
      setToast({ msg: 'Step timing saved', sev: 'success' });
      loadPipelineAndFunnels();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Could not save step', sev: 'error' });
    }
  };

  const changePromo = (f: Funnel) => {
    setPromoDialog({ open: true, funnel: f, code: f.attached_promo_code || '' });
  };

  const savePromo = async () => {
    const f = promoDialog.funnel;
    if (!f) return;
    const code = promoDialog.code.trim();
    try {
      await apiService.updateFunnelPromo(f.id, code || null);
      setToast({ msg: code ? `Offer "${code}" attached` : 'Offer detached', sev: 'success' });
      setPromoDialog({ open: false, funnel: null, code: '' });
      loadPipelineAndFunnels();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Could not update offer', sev: 'error' });
    }
  };

  const openAddStep = async (f: Funnel) => {
    setAddStepForm({ step_type: 'send_email', label: '', delay_hours: 0, include_offer: false, template_option: 'new', template_key: '' });
    setAddStepDialog({ open: true, funnel: f });
    try {
      const res = await apiService.getFunnelTemplates();
      const rows = (res as any)?.data?.templates || (res as any)?.templates || [];
      setAvailableTemplates(rows);
    } catch (_) {}
  };

  const submitAddStep = async () => {
    if (!addStepDialog.funnel) return;
    setAddStepLoading(true);
    try {
      const payload: Parameters<typeof apiService.addFunnelStep>[1] = {
        step_type: addStepForm.step_type,
        label: addStepForm.label || undefined,
        delay_hours: addStepForm.delay_hours,
        include_offer: addStepForm.include_offer,
      };
      if (['send_email', 'send_sms'].includes(addStepForm.step_type)) {
        if (addStepForm.template_option === 'existing' && addStepForm.template_key) {
          payload.template_key = addStepForm.template_key;
        } else {
          payload.create_template = true;
          payload.category = 'nurture';
        }
      }
      const res: any = await apiService.addFunnelStep(addStepDialog.funnel.id, payload);
      setAddStepDialog({ open: false, funnel: null });
      setToast({ msg: 'Step added', sev: 'success' });
      await loadPipelineAndFunnels();
      // If a new stub template was created, open the editor immediately.
      // fetchWithAuth puts the whole body under .data (the route returns no
      // `data` envelope of its own), so the old res.new_template_id was always
      // undefined and the editor never opened after creating a stub.
      const newTemplateId = res?.data?.new_template_id ?? res?.new_template_id;
      if (newTemplateId) {
        setEditTemplate({ open: true, templateId: newTemplateId });
      }
    } catch (e: any) {
      setToast({ msg: e?.message || 'Could not add step', sev: 'error' });
    } finally {
      setAddStepLoading(false);
    }
  };

  const confirmDeleteStep = (f: Funnel, s: FunnelStep) => {
    setDeleteConfirm({ open: true, funnel: f, step: s });
  };

  const executeDeleteStep = async () => {
    if (!deleteConfirm.funnel || !deleteConfirm.step) return;
    try {
      await apiService.deleteFunnelStep(deleteConfirm.funnel.id, deleteConfirm.step.id);
      setDeleteConfirm({ open: false, funnel: null, step: null });
      setToast({ msg: 'Step deleted', sev: 'success' });
      loadPipelineAndFunnels();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Could not delete step', sev: 'error' });
    }
  };

  const duplicateStep = async (f: Funnel, s: FunnelStep) => {
    try {
      await apiService.duplicateFunnelStep(f.id, s.id);
      setToast({ msg: 'Step duplicated', sev: 'success' });
      loadPipelineAndFunnels();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Could not duplicate step', sev: 'error' });
    }
  };

  const reorderStep = async (f: Funnel, steps: FunnelStep[], idx: number, direction: 'left' | 'right') => {
    const allSteps = f.steps.filter(s => ['send_email', 'send_sms', 'send_push'].includes(s.step_type));
    const sendIdx = allSteps.indexOf(steps[idx]);
    const swapIdx = direction === 'left' ? sendIdx - 1 : sendIdx + 1;
    if (swapIdx < 0 || swapIdx >= allSteps.length) return;
    const a = allSteps[sendIdx];
    const b = allSteps[swapIdx];
    try {
      await apiService.updateFunnelSteps(f.id, [
        { id: a.id, step_order: b.step_order },
        { id: b.id, step_order: a.step_order },
      ]);
      loadPipelineAndFunnels();
    } catch (e: any) {
      setToast({ msg: 'Could not reorder step', sev: 'error' });
    }
  };

  const saveStepField = async (f: Funnel, s: FunnelStep, patch: Record<string, any>) => {
    try {
      await apiService.updateFunnelSteps(f.id, [{ id: s.id, ...patch }]);
      loadPipelineAndFunnels();
    } catch (e: any) {
      setToast({ msg: 'Could not save', sev: 'error' });
    }
  };


  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#1A5F7A' }} /></Box>;
  }

  // "All funnels paused" is a CLAIM about production, so it may only be made
  // when the pipeline actually loaded. `pipeline?.active_funnels ?? 0` asserted
  // it after a failed fetch too — a 500 rendered a red "⚠ All funnels paused"
  // badge over a fleet that was running fine.
  const pipelineLoaded = pipeline != null;
  const allPaused = pipelineLoaded && (pipeline.active_funnels ?? 0) === 0;
  const totalFunnels = pipeline?.total_funnels ?? funnels.length;
  const funnelStatusLabel = !pipelineLoaded
    ? 'Funnel status unavailable'
    : allPaused
      ? `⚠ All ${totalFunnels} funnels paused`
      : `${pipeline.active_funnels} of ${totalFunnels} funnels active`;

  return (
    <PillarPageShell
      pillar="marketing"
      title="Funnel Manager"
      subtitle="Every nurture sequence — activate, re-time, attach offers, and jump to the template each step sends"
      rightActions={
        <Button size="small" variant="outlined" startIcon={<GroupIcon />}
          onClick={() => navigate('/dashboard/free-tier')}
          sx={{ textTransform: 'none', borderColor: LINE, color: '#1A5F7A' }}>
          Free Tier Members
        </Button>
      }
    >
      <Box sx={{ pb: 6 }}>

      {/* 1. EMAIL ARCHITECTURE V4 — the five personas and the diagnostic layer.
          Additive: sits above the existing pipeline and step cards rather than
          replacing them (Plan 15: extend the card pattern, do not replace it). */}
      <V4ArchitecturePanel flows={funnels} />

      {/* 2. ACTIVATION PIPELINE RIBBON */}
      <Paper sx={{ borderRadius: 3, border: `1px solid ${LINE}`, mb: 2.5, overflow: 'hidden' }}>
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${LINE}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 19, color: '#1A5F7A' }}>The Activation Pipeline</Typography>
            <Typography sx={{ fontSize: 13, color: SLATE }}>Where every free Member sits · each phase is owned by one nurture funnel. Click a phase to open its funnel.</Typography>
          </Box>
          <Chip
            label={funnelStatusLabel}
            sx={{
              bgcolor: !pipelineLoaded ? '#EAEEF1' : allPaused ? '#F1E3E7' : '#E3F1EC',
              color: !pipelineLoaded ? SLATE : allPaused ? '#7D1935' : '#146152',
              fontWeight: 700, fontSize: 11,
            }}
          />
        </Box>
        {/* Equal-width phase cards — flex-basis-0 so all 5 are identical width */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {(pipeline?.phases || []).map((ph, idx) => (
            <Box key={ph.phase}
              onClick={() => { const f = funnels.find(fn => fn.phase_key === ph.phase); if (f) setExpanded(expanded === f.id ? null : f.id); }}
              sx={{
                flex: '1 1 0', minWidth: 140,
                p: 2, cursor: 'pointer',
                borderRight: idx < ((pipeline?.phases.length || 1) - 1) ? `1px solid ${LINE}` : 'none',
                '&:hover': { bgcolor: '#F9F3E9' },
                boxSizing: 'border-box',
              }}>
              {/* Phase label row — idle badge sits inline so it never overlaps */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ph.color, flexShrink: 0 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 12.5, textTransform: 'uppercase', color: ph.color, whiteSpace: 'nowrap' }}>
                    Phase {phaseLabel(ph.phase)}
                  </Typography>
                </Box>
                {ph.idle_flag_count > 0 && (
                  <Chip label={`${ph.idle_flag_count} idle ${ph.idle_threshold_hours}h+`} size="small"
                    sx={{ bgcolor: '#FBEFE2', color: '#B45309', fontWeight: 700, fontSize: 10, height: 20, flexShrink: 0 }} />
                )}
              </Box>
              <Typography sx={{ fontSize: 11, color: SLATE }}>{ph.phase_name} · MS {ph.milestones}</Typography>
              <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: 32, my: 0.5, lineHeight: 1 }}>{ph.member_count}</Typography>
              <Typography sx={{ fontSize: 12, color: SLATE }}>{ph.pct_of_base}% of base</Typography>
              <Box sx={{ height: 6, borderRadius: 3, bgcolor: LINE, my: 1.2, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${Math.min(100, ph.pct_of_base)}%`, bgcolor: ph.color }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, fontSize: 11.5, p: 0.8, borderRadius: 2, border: `1px solid ${LINE}` }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: ph.color }} />
                <Typography sx={{ fontSize: 11.5 }}>{ph.funnel_key}</Typography>
                <Chip label={ph.funnel?.status === 'active' ? 'On' : 'Paused'} size="small"
                  sx={{ ml: 'auto', height: 18, fontSize: 10, fontWeight: 700,
                    bgcolor: ph.funnel?.status === 'active' ? '#E3F1EC' : '#F1E3E7',
                    color: ph.funnel?.status === 'active' ? '#146152' : '#7D1935' }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* 3. NURTURE FUNNEL CONTROLS */}
      <Paper sx={{ borderRadius: 3, border: `1px solid ${LINE}`, mb: 2.5, overflow: 'hidden' }}>
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${LINE}` }}>
          <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 19, color: '#1A5F7A' }}>Nurture Funnel Controls</Typography>
          <Typography sx={{ fontSize: 13, color: SLATE }}>Activate, pause, re-time every step, and attach offers. Each funnel maps to one activation phase.</Typography>
        </Box>
        <Box sx={{ p: 2.5, display: 'grid', gap: 2 }}>
          {funnels.map((f, idx) => {
            const color = f.slug === 'account_deletion_winback' ? '#7D1935' : PHASE_COLOR[f.phase_key || '1'];
            const sendSteps = f.steps.filter(s => ['send_email', 'send_sms', 'send_push'].includes(s.step_type));
            const isOpen = expanded === f.id;
            const grp = funnelGroup(f);
            const showPreDivider = idx === 0 && grp === 'pre';
            const showPostDivider = grp === 'post' && (idx === 0 || funnelGroup(funnels[idx - 1]) !== 'post');
            return (
              <React.Fragment key={f.id}>
                {showPreDivider && <SectionDivider label="PRE-SUBSCRIBER · Conversion" sublabel="The F-series — bring Members to their first paid Chapter" />}
                {/* The sublabels name the SERIES, not a fixed span: orderFunnels
                    sorts every F… and every P… it is given, so a hardcoded
                    "P1–P3" went stale the moment P4 shipped. */}
                {showPostDivider && <SectionDivider label="POST-SUBSCRIBER · Retention" sublabel="The P-series — keep paying Members recording" />}
              <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden', bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, background: 'linear-gradient(0deg,#fff,#F9F3E9)', cursor: 'pointer', flexWrap: 'wrap' }}
                  onClick={() => setExpanded(isOpen ? null : f.id)}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Playfair Display, serif', fontWeight: 800, color: '#fff', fontSize: 18, bgcolor: color, flexShrink: 0 }}>
                    {funnelBadge(f)}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 17 }}>{funnelDisplayName(f.name)}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: SLATE }}>
                      {f.phase_key ? `Phase ${phaseLabel(f.phase_key)}` : (f.slug === 'account_deletion_winback' ? 'Win-back · in-grace-window' : 'Re-engagement')} · {sendSteps.length} touch{sendSteps.length === 1 ? '' : 'es'}
                      {f.template_health.stubs > 0 && <span style={{ color: '#7D1935', fontWeight: 600 }}> · {f.template_health.stubs} stub template(s)</span>}
                    </Typography>
                    {/* Who this sequence mails, and how it performs — the two
                        questions the old card could not answer. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                      {f.audience ? (
                        <Tooltip title="The audience that mirrors this sequence's entry rule — open it" arrow>
                          <Chip size="small" icon={<GroupIcon sx={{ fontSize: 13 }} />}
                            label={`${f.audience.name} · ${f.audience.count.toLocaleString()}`}
                            onClick={() => navigate(audienceDetailPath(f.audience!.id))}
                            sx={{ height: 22, fontSize: 11, bgcolor: '#F0F7FA', color: '#1A5F7A', border: `1px solid ${LINE}`, cursor: 'pointer' }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="This sequence fires on an event (no standing population to list)" arrow>
                          <Chip size="small" label="event-driven · no standing audience"
                            sx={{ height: 22, fontSize: 11, bgcolor: '#F5F2EA', color: SLATE, border: `1px solid ${LINE}` }} />
                        </Tooltip>
                      )}
                      <Tooltip title="Sends, delivery, opens and clicks for this sequence" arrow>
                        <Chip size="small" icon={<InsightsIcon sx={{ fontSize: 13 }} />} label="Performance"
                          onClick={() => navigate(`/dashboard/communications/marketing/overview?sequence=${encodeURIComponent(f.slug)}`)}
                          sx={{ height: 22, fontSize: 11, bgcolor: '#F5F2EA', color: '#1A5F7A', border: `1px solid ${LINE}`, cursor: 'pointer' }} />
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, ml: 'auto', mr: 2 }}>
                    {([
                      { l: 'Eligible', n: f.eligible_count, onClick: () => openEligibleDialog(f), tip: 'View members eligible for this funnel' },
                      { l: 'Enrolled', n: f.active_enrollments, onClick: null, tip: '' },
                      { l: 'Steps', n: f.step_count, onClick: () => setStepsDialog(f), tip: 'View activation-flow steps this funnel covers' },
                    ] as const).map((box) => {
                      const clickable = !!box.onClick;
                      const content = (
                        <Box
                          key={box.l}
                          onClick={clickable ? (e) => { e.stopPropagation(); box.onClick!(); } : undefined}
                          sx={{
                            textAlign: 'center', borderRadius: 1.5, px: 1, py: 0.4,
                            cursor: clickable ? 'pointer' : 'default',
                            transition: 'background 120ms',
                            ...(clickable && { '&:hover': { bgcolor: '#F1E9D6' } }),
                          }}
                        >
                          <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: 20, lineHeight: 1, color: clickable ? '#1A5F7A' : INK }}>{box.n}</Typography>
                          <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.4px', color: SLATE, mt: 0.4, textDecoration: clickable ? 'underline dotted' : 'none' }}>{box.l}</Typography>
                        </Box>
                      );
                      return clickable ? <Tooltip key={box.l} title={box.tip} arrow>{content}</Tooltip> : content;
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <Switch checked={f.status === 'active'} onChange={() => toggleFunnel(f)} color="success" />
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: f.status === 'active' ? '#146152' : '#7D1935' }}>
                      {f.status === 'active' ? 'Active' : 'Paused'}
                    </Typography>
                  </Box>
                </Box>

                <Collapse in={isOpen}>
                  <Box sx={{ p: 2.5, pt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                      {sendSteps.map((s, idx) => (
                        <Box key={s.id} sx={{ minWidth: 220, border: `1px solid ${LINE}`, borderRadius: 2.5, p: 1.5, flexShrink: 0, position: 'relative', bgcolor: '#fff' }}>
                          {/* Step action toolbar */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 1, ml: -0.5 }}>
                            <Tooltip title="Move left" arrow>
                              <span><IconButton size="small" disabled={idx === 0}
                                onClick={() => reorderStep(f, sendSteps, idx, 'left')}
                                sx={{ color: SLATE, '&:not(:disabled):hover': { color: '#1A5F7A' } }}>
                                <ArrowLeftIcon sx={{ fontSize: 14 }} />
                              </IconButton></span>
                            </Tooltip>
                            <Tooltip title="Move right" arrow>
                              <span><IconButton size="small" disabled={idx === sendSteps.length - 1}
                                onClick={() => reorderStep(f, sendSteps, idx, 'right')}
                                sx={{ color: SLATE, '&:not(:disabled):hover': { color: '#1A5F7A' } }}>
                                <ArrowRightIcon sx={{ fontSize: 14 }} />
                              </IconButton></span>
                            </Tooltip>
                            <Tooltip title="Duplicate step" arrow>
                              <IconButton size="small" onClick={() => duplicateStep(f, s)}
                                sx={{ color: SLATE, '&:hover': { color: '#1A5F7A' } }}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete step" arrow>
                              <IconButton size="small" onClick={() => confirmDeleteStep(f, s)}
                                sx={{ color: SLATE, ml: 'auto', '&:hover': { color: '#7D1935' } }}>
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {/* Delay hours */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography sx={{ fontSize: 12, color: SLATE, fontWeight: 600 }}>Send at</Typography>
                            <TextField defaultValue={s.delay_hours ?? 0} type="number" size="small"
                              onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v) && v !== (s.delay_hours ?? 0)) saveStepDelay(f, s, v); }}
                              sx={{ width: 68, '& input': { textAlign: 'center', fontWeight: 700, color: '#1A5F7A', py: 0.5 } }} />
                            <Typography sx={{ fontSize: 12, color: SLATE, fontWeight: 600 }}>hrs idle</Typography>
                          </Box>

                          {/* Step type selector */}
                          <Box sx={{ mb: 0.8 }}>
                            <Select size="small" value={s.step_type}
                              onChange={(e) => saveStepField(f, s, { step_type: e.target.value })}
                              sx={{ fontSize: 11, fontWeight: 700, height: 28, color: '#1A5F7A', '& .MuiSelect-select': { py: 0.4, pl: 1 } }}>
                              <MenuItem value="send_email">✉ Email</MenuItem>
                              <MenuItem value="send_sms">✶ SMS</MenuItem>
                              <MenuItem value="send_push">🔔 Push</MenuItem>
                              <MenuItem value="wait">⏱ Wait</MenuItem>
                              <MenuItem value="exit_if">⛔ Exit if</MenuItem>
                            </Select>
                          </Box>

                          {/* Label (inline edit) */}
                          <TextField
                            defaultValue={s.label || ''}
                            placeholder={s.template_key || 'Step label…'}
                            size="small"
                            fullWidth
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (s.label || '')) saveStepField(f, s, { label: v || null });
                            }}
                            sx={{ mb: 1, '& input': { fontSize: 13, fontWeight: 600, py: 0.6 } }}
                          />

                          {/* Per-step offer toggle (offer-capable funnels only) */}
                          {isOfferCapable(f) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Switch size="small" checked={s.include_offer}
                                onChange={(e) => saveStepField(f, s, { include_offer: e.target.checked })}
                                sx={{ '& .MuiSwitch-thumb': { width: 12, height: 12 }, '& .MuiSwitch-switchBase': { p: '5px' } }} />
                              <Typography sx={{ fontSize: 11, color: s.include_offer ? '#146152' : SLATE, fontWeight: 600 }}>
                                {s.include_offer ? '🎟 Offer on' : 'No offer'}
                              </Typography>
                            </Box>
                          )}

                          {/* WHICH TEMPLATE THIS STEP SENDS.
                              Until 2026-08-19 the key appeared only as the
                              placeholder of the label field (so it vanished the
                              moment a label existed) and the subject line was
                              fetched by the API and never rendered at all —
                              there was no way to tell what a step actually
                              sends, and no way to reach the template. */}
                          {s.template_key && (
                            <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px dashed ${LINE}` }}>
                              <Tooltip title="Open this template's performance page" arrow>
                                <Typography
                                  component="button"
                                  onClick={() => navigate(templatePerformancePath(s.template_key!))}
                                  sx={{
                                    display: 'block', width: '100%', textAlign: 'left', border: 0, p: 0,
                                    background: 'none', cursor: 'pointer',
                                    fontFamily: 'monospace', fontSize: 11, color: '#1A5F7A',
                                    textDecoration: 'underline', textDecorationStyle: 'dotted',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }}>
                                  {s.template_key}
                                </Typography>
                              </Tooltip>
                              {s.subject_line && (
                                <Tooltip title={s.subject_line} arrow>
                                  <Typography sx={{ fontSize: 11, color: SLATE, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    “{s.subject_line}”
                                  </Typography>
                                </Tooltip>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Chip size="small" label={s.template_ready ? '● Ready' : '● Stub'}
                                  sx={{ height: 18, fontSize: 10, fontWeight: 600,
                                    bgcolor: s.template_ready ? '#E3F1EC' : '#FBE3E3', color: s.template_ready ? '#146152' : '#7D1935' }} />
                                <Tooltip title={s.template_id ? 'Edit template content here' : 'Template not yet created'} arrow>
                                  <span style={{ marginLeft: 'auto' }}>
                                    <IconButton size="small" disabled={!s.template_id}
                                      onClick={() => s.template_id && setEditTemplate({ open: true, templateId: s.template_id })}
                                      sx={{ color: '#1A5F7A' }}>
                                      <EditIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))}

                      {/* Add Step tile */}
                      <Box onClick={() => openAddStep(f)}
                        sx={{ minWidth: 120, border: `2px dashed ${LINE}`, borderRadius: 2.5, p: 1.5, flexShrink: 0,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', color: SLATE, gap: 0.5,
                          '&:hover': { borderColor: '#1A5F7A', color: '#1A5F7A', bgcolor: '#F0F7FA' },
                          transition: 'all 120ms' }}>
                        <AddIcon sx={{ fontSize: 22 }} />
                        <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>Add step</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 1.5, mt: 1.5, borderTop: `1px dashed ${LINE}`, flexWrap: 'wrap' }}>
                      {isOfferCapable(f) && (
                        <>
                          <Chip label={f.attached_promo_code ? `🎟 Offer: ${f.attached_promo_code}` : 'No offer attached'}
                            sx={{ bgcolor: '#F9F3E9', border: `1px solid ${LINE}`, fontWeight: 600, fontSize: 12.5 }} />
                          <Button size="small" variant="outlined" onClick={() => changePromo(f)} sx={{ color: '#1A5F7A', borderColor: LINE, textTransform: 'none' }}>
                            {f.attached_promo_code ? 'Change offer' : 'Attach offer'}
                          </Button>
                        </>
                      )}
                      <Button size="small" variant="contained" onClick={() => toggleFunnel(f)}
                        sx={{ ml: 'auto', textTransform: 'none', bgcolor: f.status === 'active' ? '#7D1935' : '#1A5F7A' }}>
                        {f.status === 'active' ? 'Pause Funnel' : '▶ Activate Funnel'}
                      </Button>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
              </React.Fragment>
            );
          })}
          {funnels.length === 0 && (
            <Typography sx={{ color: SLATE, textAlign: 'center', py: 3 }}>
              No nurture funnels found.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Edit a step's email template (reuses the Communications editor). */}
      <MarketingTemplateEditorDialog
        open={editTemplate.open}
        mode="edit"
        templateId={editTemplate.templateId}
        onClose={() => setEditTemplate({ open: false, templateId: null })}
        onSaved={() => { setToast({ msg: 'Template saved', sev: 'success' }); loadPipelineAndFunnels(); }}
      />
      {/* "Eligible" → members currently eligible for this funnel */}
      <Dialog open={!!eligibleDialog} onClose={() => setEligibleDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          Eligible members — {funnelDisplayName(eligibleDialog?.name || '')}
          <IconButton size="small" aria-label="Close eligible members dialog" onClick={() => setEligibleDialog(null)} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {eligibleLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
          {!eligibleLoading && eligibleMembers.length === 0 && (
            <Typography sx={{ color: SLATE, py: 3, px: 2 }}>No eligible members found for this funnel.</Typography>
          )}
          {!eligibleLoading && eligibleMembers.map((m, i) => (
            <Box key={m.user_id} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.4,
              borderBottom: i < eligibleMembers.length - 1 ? `1px solid ${LINE}` : 'none' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK }}>{m.name || '(no name)'}</Typography>
                <Typography sx={{ fontSize: 12.5, color: SLATE }}>{m.email || '—'}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                {m.hours_idle != null && (
                  <Typography sx={{ fontSize: 11.5, color: SLATE }}>{Math.round(m.hours_idle)}h idle</Typography>
                )}
                {m.engagement_tier && (
                  <Chip size="small" label={m.engagement_tier}
                    sx={{ height: 18, fontSize: 10, fontWeight: 700, mt: 0.3,
                      bgcolor: TIER_COLOR[m.engagement_tier]?.bg || '#EAEEF1',
                      color: TIER_COLOR[m.engagement_tier]?.fg || SLATE }} />
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Typography sx={{ fontSize: 12, color: SLATE, mr: 'auto', pl: 1 }}>
            {eligibleMembers.length} member{eligibleMembers.length !== 1 ? 's' : ''}
          </Typography>
          <Button onClick={() => setEligibleDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* "Steps" → activation-flow steps this funnel covers */}
      <Dialog open={!!stepsDialog} onClose={() => setStepsDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          Activation steps covered — {funnelDisplayName(stepsDialog?.name || '')}
          <IconButton size="small" aria-label="Close activation steps dialog" onClick={() => setStepsDialog(null)} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const ph = pipeline?.phases?.find(p => p.phase === stepsDialog?.phase_key);
            const labels = ph?.step_labels || [];
            if (!labels.length) return <Typography sx={{ color: SLATE, py: 2 }}>No activation steps mapped for this phase.</Typography>;
            return (
              <>
                <Typography sx={{ fontSize: 13, color: SLATE, mb: 1.5 }}>
                  Phase {phaseLabel(stepsDialog?.phase_key)} · {ph?.phase_name} — the user-activation milestones this funnel nurtures members through:
                </Typography>
                {labels.map((label, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9 }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, bgcolor: PHASE_COLOR[stepsDialog?.phase_key || '1'],
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{i + 1}</Box>
                    <Typography sx={{ fontSize: 14 }}>{label}</Typography>
                  </Box>
                ))}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions><Button onClick={() => setStepsDialog(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Add Step Dialog */}
      <Dialog open={addStepDialog.open} onClose={() => setAddStepDialog({ open: false, funnel: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          Add step — {funnelDisplayName(addStepDialog.funnel?.name || '')}
          <IconButton size="small" aria-label="Close add step dialog" onClick={() => setAddStepDialog({ open: false, funnel: null })} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Step type</InputLabel>
            <Select label="Step type" value={addStepForm.step_type}
              onChange={(e) => setAddStepForm(f => ({ ...f, step_type: e.target.value }))}>
              <MenuItem value="send_email">✉ Send Email</MenuItem>
              <MenuItem value="send_sms">✶ Send SMS</MenuItem>
              <MenuItem value="send_push">🔔 Send Push</MenuItem>
              <MenuItem value="wait">⏱ Wait</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" fullWidth label="Label (optional)"
            value={addStepForm.label}
            onChange={(e) => setAddStepForm(f => ({ ...f, label: e.target.value }))} />
          <TextField size="small" type="number" fullWidth label="Send at (hrs idle)"
            value={addStepForm.delay_hours}
            onChange={(e) => setAddStepForm(f => ({ ...f, delay_hours: parseInt(e.target.value, 10) || 0 }))} />
          <FormControlLabel
            control={<Switch checked={addStepForm.include_offer} onChange={(e) => setAddStepForm(f => ({ ...f, include_offer: e.target.checked }))} />}
            label="Attach funnel offer" />
          {['send_email', 'send_sms'].includes(addStepForm.step_type) && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Template</InputLabel>
                <Select label="Template" value={addStepForm.template_option}
                  onChange={(e) => setAddStepForm(f => ({ ...f, template_option: e.target.value, template_key: '' }))}>
                  <MenuItem value="new">✦ Create new stub template</MenuItem>
                  <MenuItem value="existing">Use existing template</MenuItem>
                </Select>
              </FormControl>
              {addStepForm.template_option === 'existing' && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Existing template</InputLabel>
                  <Select label="Existing template" value={addStepForm.template_key}
                    onChange={(e) => setAddStepForm(f => ({ ...f, template_key: e.target.value }))}>
                    {availableTemplates.map(t => (
                      <MenuItem key={t.id} value={t.template_key}>{t.name || t.template_key}</MenuItem>
                    ))}
                    {availableTemplates.length === 0 && <MenuItem disabled>No templates found</MenuItem>}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setAddStepDialog({ open: false, funnel: null })} sx={{ color: SLATE }}>Cancel</Button>
          <Button variant="contained" onClick={submitAddStep} disabled={addStepLoading}
            sx={{ bgcolor: '#1A5F7A', '&:hover': { bgcolor: '#154e64' }, textTransform: 'none', fontWeight: 700 }}>
            {addStepLoading ? 'Adding…' : 'Add step'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Step Confirm */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, funnel: null, step: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700 }}>Delete step?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: SLATE }}>
            Remove <b>{deleteConfirm.step?.label || deleteConfirm.step?.template_key || `Step ${deleteConfirm.step?.step_order}`}</b> from{' '}
            <b>{funnelDisplayName(deleteConfirm.funnel?.name || '')}</b>?
            Any active member enrollments at this step will be unblocked (step reference cleared).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, funnel: null, step: null })} sx={{ color: SLATE }}>Cancel</Button>
          <Button variant="contained" onClick={executeDeleteStep}
            sx={{ bgcolor: '#7D1935', '&:hover': { bgcolor: '#641428' }, textTransform: 'none', fontWeight: 700 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Attach Offer Dialog */}
      <Dialog open={promoDialog.open} onClose={() => setPromoDialog({ open: false, funnel: null, code: '' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, pb: 1 }}>
          Attach Offer Code
          <Typography sx={{ fontSize: 12, color: SLATE, fontWeight: 400, mt: 0.25 }}>
            {promoDialog.funnel?.name}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2.5, p: 2, bgcolor: '#F0F7FA', borderRadius: 2, border: `1px solid #C5DDE8` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1A5F7A', mb: 0.75 }}>How it works</Typography>
            <Typography sx={{ fontSize: 13, color: '#2C3E50', lineHeight: 1.6 }}>
              Members subscribe through the <b>iOS App Store or Google Play</b>, so offer codes come
              from those stores — not Stripe. You create the code in the app store, paste it here,
              and it gets merged into any funnel step that has <b>"Offer on"</b> toggled, so the member
              sees their discount code in the email automatically.
            </Typography>
          </Box>
          <Box sx={{ mb: 2, p: 2, bgcolor: '#FBF9F0', borderRadius: 2, border: `1px solid #E8DFC0` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#9A7B0A', mb: 0.5 }}>Where to create the code</Typography>
            <Typography sx={{ fontSize: 13, color: '#2C3E50', lineHeight: 1.8 }}>
              <b>iOS (App Store Connect)</b><br />
              Apps → LifeScribe → Subscriptions → [select plan] → <b>Offer Codes</b> → Create<br />
              Copy the generated code and paste it below.<br /><br />
              <b>Android (Google Play Console)</b><br />
              Monetize → Subscriptions → [select plan] → <b>Promo codes</b> → Create<br />
              Copy the promo code and paste it below.
            </Typography>
          </Box>
          <TextField
            label="Offer Code"
            placeholder="e.g. LIFESCRIBE30OFF"
            value={promoDialog.code}
            onChange={(e) => setPromoDialog(d => ({ ...d, code: e.target.value }))}
            fullWidth
            size="small"
            helperText={promoDialog.funnel?.attached_promo_code
              ? `Currently attached: ${promoDialog.funnel.attached_promo_code} — clear the field and save to detach`
              : 'Paste the code exactly as it appears in App Store Connect or Google Play Console'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button onClick={() => setPromoDialog({ open: false, funnel: null, code: '' })} sx={{ color: SLATE, textTransform: 'none' }}>Cancel</Button>
          {promoDialog.funnel?.attached_promo_code && (
            <Button onClick={() => setPromoDialog(d => ({ ...d, code: '' }))} sx={{ color: '#7D1935', textTransform: 'none' }}>Clear code</Button>
          )}
          <Button variant="contained" onClick={savePromo}
            sx={{ bgcolor: '#1A5F7A', '&:hover': { bgcolor: '#164E65' }, textTransform: 'none', fontWeight: 700 }}>
            {promoDialog.code.trim() ? 'Attach offer' : 'Detach offer'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
    </PillarPageShell>
  );
}
